/**
 * Client-side email pre-validation for sign-up. This is the fast, offline first pass that
 * gives instant feedback in the modal; the AUTHORITATIVE checks (disposable-domain blocklist
 * + live MX-record lookup + the one-time code itself) run in the Worker, which cannot be
 * bypassed. Keep this in sync in spirit with worker/src/emailAuth.ts, but the Worker is the
 * gate that actually decides whether a code is sent.
 */

/** Lower-case + trim; strips nothing else (dots/plus in the local part can be significant). */
export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * A pragmatic RFC-5321-ish syntax check: one @, a non-empty local part with no spaces, and a
 * domain with at least one dot and a 2+ char TLD. Deliberately not a full RFC grammar (those
 * accept addresses no mail server would) — this rejects the shapes users actually fat-finger.
 */
export function isValidEmailSyntax(raw: string): boolean {
  const email = normalizeEmail(raw);
  if (email.length < 6 || email.length > 254) return false;
  if ((email.match(/@/g) || []).length !== 1) return false;
  const [local, domain] = email.split("@");
  if (!local || local.length > 64) return false;
  if (/\s/.test(email)) return false;
  if (/^[.]|[.]$|[.]{2}/.test(local)) return false; // no leading/trailing/double dots in local
  if (!/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+$/.test(local)) return false;
  if (domain.length > 253 || domain.startsWith(".") || domain.endsWith(".")) return false;
  if (domain.includes("..")) return false;
  const labels = domain.split(".");
  if (labels.length < 2) return false;
  if (!/^[a-z]{2,}$/.test(labels[labels.length - 1])) return false; // real-looking TLD
  return labels.every((l) => /^[a-z0-9-]{1,63}$/.test(l) && !l.startsWith("-") && !l.endsWith("-"));
}

/** The single reason to surface to the user, or "" if the syntax pass is clean. */
export function emailSyntaxError(raw: string): string {
  return isValidEmailSyntax(raw) ? "" : "Enter a valid email address.";
}
